package com.devi3east.bashbuddy.features.events.feed.ui

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.snapping.rememberSnapFlingBehavior
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyGridScope
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.itemsIndexed
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.account.profile.domain.models.Profile
import com.devi3east.bashbuddy.auth.domain.models.UserType
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.InfoAlertDialog
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.features.events.common.data.models.Event
import com.devi3east.bashbuddy.features.events.common.data.models.EventStatus
import com.devi3east.bashbuddy.features.events.feed.ui.components.EmptyEventFeedContent
import com.devi3east.bashbuddy.features.events.feed.ui.components.EventCard
import com.devi3east.bashbuddy.features.events.feed.ui.components.FeaturedEventCard
import kotlinx.coroutines.delay
import kotlinx.datetime.Clock

@Composable
fun EventFeedRoute(
    appState: AppState,
    navigateToEventDetail: (String, String) -> Unit
) {
    val authState by appState.authViewModel.uiState.collectAsState()
    val profile = authState.profile

    val viewModel = appState.eventFeedViewModel
    val uiState by viewModel.uiState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.getEventsList()
        viewModel.getUserCount()
    }

    EventFeedScreen(
        profile = profile,
        uiState = uiState,
        navigateToEventDetail = navigateToEventDetail,
        onConfirmFirstSignIn = { updatedProfile ->
            appState.authViewModel.updateProfile(updatedProfile)
        },
        eventFeedViewModel = viewModel
    )

}

@Composable
fun EventFeedScreen(
    profile: Profile?,
    uiState: EventFeedUiState,
    navigateToEventDetail: (String, String) -> Unit,
    onConfirmFirstSignIn: (Profile) -> Unit,
    eventFeedViewModel: EventFeedViewModel
) {
    var showFirstSignInDialog by remember(profile) {
        mutableStateOf(
            profile?.showFirstSignInDialog == true
        )
    }

    val visibleEvents = uiState.eventsList.filter {
        it.status == EventStatus.UPCOMING ||
                it.status == EventStatus.LIVE ||
                it.status == EventStatus.COMPLETED
    }

    Scaffold(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background),
    ) { innerPadding ->
        when {
            !uiState.hasCompletedInitialLoad -> {

            }

            uiState.isLoading -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator()
                }
            }

            uiState.errorMessage != null -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = uiState.errorMessage,
                        modifier = Modifier.clickable {
                            eventFeedViewModel.getEventsList()
                        }
                    )
                }
            }

            visibleEvents.isEmpty() -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    contentAlignment = Alignment.Center
                ) {
                    EmptyEventFeedContent()
                }
            }

            else -> {
                EventFeedContent(
                    events = visibleEvents,
                    navigateToEventDetail = navigateToEventDetail
                )
            }
        }

        if (
            profile != null &&
            showFirstSignInDialog &&
            profile.userType == UserType.ORGANIZATION
        ) {
            InfoAlertDialog(
                title = "Welcome — let’s get your organization ready!",
                message = "We’re excited to help you share your events with your community. To activate your organization and qualify for the promotion, complete Stripe onboarding by visiting Organization -> Stripe, then post your first upcoming event. Once that’s done, you’re officially in!",
                onDismiss = {
                    showFirstSignInDialog = false
                    onConfirmFirstSignIn(
                        profile.copy(showFirstSignInDialog = false)
                    )
                },
                confirmText = "OK"
            )
        }
    }
}

@Composable
private fun EventFeedContent(
    navigateToEventDetail: (String, String) -> Unit,
    events: List<Event>
) {
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val screenWidth = maxWidth

        // Mode 1 responsive breakpoints:
        val columns = when {
            screenWidth < 600.dp -> 2
            screenWidth <= 900.dp -> 3
            else -> 4
        }

        // horizontal padding increases on larger screens to center content
        val horizontalPadding = when {
            screenWidth < 600.dp -> 0.dp
            screenWidth <= 900.dp -> 48.dp
            else -> 96.dp
        }

        // featured card width fraction: 85% on mobile, narrower on larger screens
        val featuredCardWidthFraction = when {
            screenWidth < 600.dp -> 0.85f
            screenWidth <= 900.dp -> 0.55f
            else -> 0.40f
        }

        var selectedTab by remember { mutableStateOf("Upcoming") }
        val toggleOptions = listOf("Upcoming", "Completed")

        val now = Clock.System.now()
        val upcomingEvents = events.filter { event ->
            val end = event.endInstant
            end == null || end > now
        }
        val completedEvents = events.filter { event ->
            val end = event.endInstant
            end != null && end <= now
        }

        LazyVerticalGrid(
            columns = GridCells.Fixed(columns),
            verticalArrangement = Arrangement.spacedBy(8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            contentPadding = PaddingValues(
                start = horizontalPadding,
                end = horizontalPadding,
                top = 12.dp,
                bottom = 24.dp
            ),
            modifier = Modifier.fillMaxSize()
        ) {
            // --- Featured Events (Upcoming Only) ---
            item(
                span = { GridItemSpan(maxLineSpan) }
            ) {
                if (upcomingEvents.isNotEmpty()) {
                    FeaturedEventsList(
                        navigateToEventDetail = navigateToEventDetail,
                        events = upcomingEvents.sortedByDescending { it.viewsCount },
                        widthFraction = featuredCardWidthFraction
                    )
                }
            }

            // --- Toggle Row ("Upcoming", "Completed") ---
            item(
                span = { GridItemSpan(maxLineSpan) }
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 8.dp),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    toggleOptions.forEach { label ->
                        val isSelected = selectedTab == label

                        val backgroundColor by animateColorAsState(
                            if (isSelected)
                                MaterialTheme.colorScheme.primaryContainer
                            else
                                MaterialTheme.colorScheme.surface.copy(alpha = 0.6f)
                        )

                        val textColor by animateColorAsState(
                            if (isSelected)
                                MaterialTheme.colorScheme.onPrimaryContainer
                            else
                                MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                        )

                        val cardShape = RoundedCornerShape(12.dp)

                        Surface(
                            color = backgroundColor,
                            shape = cardShape,
                            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                            modifier = Modifier
                                .weight(1f)
                                .clip(cardShape)
                                .clickable {
                                    selectedTab = label
                                }
                                .heightIn(48.dp)
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                .padding(vertical = 8.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = label,
                                    color = textColor,
                                    style = MaterialTheme.typography.bodyLarge,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }
                    }
                }
            }

            // --- Event Lists Based on Toggle ---
            when (selectedTab) {
                "Upcoming" -> {
                    // pass columns so card padding aligns correctly
                    upcomingEventsSection(navigateToEventDetail = navigateToEventDetail, events = upcomingEvents, columns = columns)
                }

                "Completed" -> {
                    upcomingEventsSection(navigateToEventDetail = navigateToEventDetail, events = completedEvents, columns = columns)
                }
            }
        }
    }
}

/**
 * FeaturedEventsList adjusted to accept widthFraction (keeps your original 85% behavior on mobile
 * and scales down on larger screens).
 */
@Composable
private fun FeaturedEventsList(
    navigateToEventDetail: (String, String) -> Unit,
    events: List<Event>,
    widthFraction: Float
) {
    Column {
        val listState = rememberLazyListState()

        // Auto-scroll coroutine (keeps exact behavior)
        LaunchedEffect(listState, events) {
            if (events.isEmpty()) return@LaunchedEffect

            while (true) {
                delay(4000)
                val nextIndex = (listState.firstVisibleItemIndex + 1) % events.size
                listState.animateScrollToItem(nextIndex)
            }
        }

        BoxWithConstraints {
            val screenWidth = maxWidth
            val cardWidth = screenWidth * widthFraction
            val edgePeek = (screenWidth - cardWidth) / 2

            LazyRow(
                state = listState,
                contentPadding = PaddingValues(horizontal = edgePeek),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                flingBehavior = rememberSnapFlingBehavior(listState) // snaps to center
            ) {
                items(events, key = { it._id }) { event ->
                    FeaturedEventCard(
                        event = event,
                        onClick = { navigateToEventDetail(event._id, event.slug) },
                        modifier = Modifier
                            .width(cardWidth)
                    )
                }
            }
        }
    }
}

/**
 * Produces the grid items for upcoming/completed lists.
 * Accepts columns to compute left/right padding per item so visuals match original spacing.
 */
private fun LazyGridScope.upcomingEventsSection(
    navigateToEventDetail: (String, String) -> Unit,
    events: List<Event>,
    columns: Int
) {

    // if no events, render nothing (the calling composable handles empty states)
    itemsIndexed(events, key = { _, it -> it._id }) { index, event ->

        // compute column index (0..columns-1) and set left/right padding similar to original file
        val columnIndex = index % columns

        // original file used 16.dp start on left column, 0 on right; keep the pattern but generalized:
        val startPadding = if (columnIndex == 0) 16.dp else 0.dp
        val endPadding = if (columnIndex == columns - 1) 16.dp else 0.dp

        EventCard(
            event,
            onClick = { navigateToEventDetail(event._id, event.slug) },
            modifier = Modifier
                .padding(start = startPadding, end = endPadding)
        )
    }

}